import React from "react";

// Risk levels for dropdown
const riskLevels = [
  { value: "", label: "Select...", color: "" },
  { value: "Intolerable", label: "Intolerable / Substantial / High", color: "#d32f2f", text: "#fff" },
  { value: "Moderate", label: "Moderate", color: "#ff9800", text: "#fff" },
  { value: "Tolerable", label: "Tolerable", color: "#fffde7", text: "#444" },
  { value: "Trivial", label: "Trivial / Low", color: "#a5d6a7", text: "#222" }
];

function getRiskColor(level) {
  const entry = riskLevels.find(r => r.value.toLowerCase() === (level || "").toLowerCase());
  return entry && entry.color
    ? { background: entry.color, color: entry.text, fontWeight: "bold" }
    : {};
}

const fields = [
  { key: "priority", label: "Priority", isSelect: true },
  { key: "severity", label: "Severity", isSelect: true },
  { key: "issue", label: "Issue" },
  { key: "allocation", label: "Allocation" },
  { key: "timeScale", label: "Time Scale" },
  { key: "responsible", label: "Responsible" }
];

export default function ActionPlanForm({ actionPlan, setActionPlan }) {
  const update = (idx, key, value) => {
    const arr = [...actionPlan];
    arr[idx][key] = value;
    setActionPlan(arr);
  };

  return (
    <div className="fra-card fra-actionplan-root">
      <div className="actionplan-tablewrap">
        {/* Table for desktop, stacked cards for mobile */}
        <div className="actionplan-list" role="list">
          {actionPlan.map((item, i) => (
            <div className="actionplan-row" key={i} role="listitem">
              <div className="actionplan-row-fields">
                {fields.map(f => (
                  <div
                    className="actionplan-field"
                    key={f.key}
                    style={
                      (f.key === "priority" || f.key === "severity")
                        ? { ...getRiskColor(item[f.key]), borderRadius: 6 }
                        : {}
                    }
                  >
                    <label className="actionplan-label">
                      {f.label}
                      {f.isSelect ? (
                        <select
                          className="risk-select"
                          aria-label={f.label}
                          value={item[f.key] || ""}
                          onChange={e => update(i, f.key, e.target.value)}
                          style={getRiskColor(item[f.key])}
                        >
                          {riskLevels.map(opt => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          value={item[f.key] || ""}
                          onChange={e => update(i, f.key, e.target.value)}
                          placeholder={f.label}
                          className="risk-input"
                        />
                      )}
                    </label>
                  </div>
                ))}
              </div>
              <div className="actionplan-remove">
                <button
                  type="button"
                  className="risk-remove"
                  aria-label="Remove Action"
                  onClick={() => setActionPlan(actionPlan.filter((_, idx) => idx !== i))}
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setActionPlan([...actionPlan, {}])}
          className="risk-add-btn"
        >
          Add Action
        </button>
      </div>
      {/* Modern Color Legend */}
      <div className="fra-risk-legend">
        <h4>Risk/Warning Color Legend</h4>
        <div className="risk-legend-list">
          {riskLevels.filter(r => r.value).map(risk => (
            <div className="risk-legend-item" key={risk.value}>
              <span
                className="risk-legend-swatch"
                style={{
                  background: risk.color,
                  color: risk.text,
                  borderRadius: 4,
                  padding: "3px 14px",
                  minWidth: 80,
                  textAlign: "center"
                }}
              >
                {risk.label.split("/")[0]}
              </span>
              <span className="risk-legend-desc">
                {risk.value === "Intolerable"
                  ? "Red — Immediate action required. Do not occupy until risk is reduced."
                  : risk.value === "Moderate"
                  ? "Orange — Act to reduce risk in a defined timescale."
                  : risk.value === "Tolerable"
                  ? "Yellow — No major controls needed, consider improvements."
                  : "Green — No action required, risk acceptable."}
              </span>
            </div>
          ))}
        </div>
        <div className="risk-legend-note">
          <b>How this works:</b> The colored dropdown and backgrounds indicate the urgency of each action.
        </div>
      </div>

      {/* Responsive Styles */}
      <style>{`
        .fra-actionplan-root {
          max-width: 900px;
          margin: 0 auto;
          background: #fff;
        }
        .actionplan-tablewrap {
          width: 100%;
        }
        .actionplan-list {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          width: 100%;
        }
        .actionplan-row {
          display: flex;
          align-items: stretch;
          gap: 10px;
          background: #f8fafc;
          border-radius: 10px;
          box-shadow: 0 1px 6px #e1e5eb33;
          padding: 18px 16px 14px 16px;
          flex-wrap: wrap;
        }
        .actionplan-row-fields {
          flex: 1;
          display: flex;
          flex-wrap: wrap;
          gap: 14px 20px;
          min-width: 0;
        }
        .actionplan-field {
          min-width: 120px;
          flex: 1 1 160px;
          display: flex;
          flex-direction: column;
        }
        .actionplan-label {
          font-size: 14.5px;
          font-weight: 500;
          margin-bottom: 2px;
          color: #223;
        }
        .risk-select {
          border-radius: 6px;
          padding: 10px 7px;
          border: 1px solid #bbb;
          min-width: 115px;
          font-size: 16px;
          margin-top: 4px;
          margin-bottom: 3px;
          font-weight: bold;
          outline: none;
        }
        .risk-input {
          border-radius: 6px;
          padding: 10px 8px;
          font-size: 15px;
          border: 1px solid #bbb;
          margin-top: 4px;
          margin-bottom: 3px;
        }
        .actionplan-remove {
          display: flex;
          align-items: flex-start;
          margin-left: 5px;
        }
        .risk-remove {
          color: #c22;
          background: none;
          border: none;
          font-size: 23px;
          cursor: pointer;
          border-radius: 50%;
          width: 38px;
          height: 38px;
          line-height: 38px;
          transition: background 0.15s;
        }
        .risk-remove:hover, .risk-remove:focus {
          background: #f9e2e2;
        }
        .risk-add-btn {
          background: #236b50;
          color: #fff;
          border: none;
          border-radius: 6px;
          padding: 14px 34px;
          margin-top: 18px;
          font-size: 18px;
          font-weight: bold;
          cursor: pointer;
          box-shadow: 0 2px 10px #dbe9df44;
        }
        .risk-add-btn:hover {
          background: #16503a;
        }
        .fra-risk-legend {
          border-top: 1px solid #ececec;
          padding-top: 16px;
          margin-top: 22px;
        }
        .risk-legend-list {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          margin-bottom: 10px;
          margin-top: 10px;
        }
        .risk-legend-item {
          display: flex;
          align-items: center;
        }
        .risk-legend-swatch {
          border: 1px solid #ddd;
          font-size: 14px;
          margin-right: 7px;
          min-width: 70px;
          display: inline-block;
        }
        .risk-legend-desc {
          font-size: 15px;
        }
        .risk-legend-note {
          font-size: 14px;
          margin-top: 7px;
          color: #555;
        }
        @media (max-width: 850px) {
          .fra-actionplan-root { padding: 0 0.5rem; }
          .actionplan-row-fields { gap: 14px 6px; }
        }
        @media (max-width: 650px) {
          .actionplan-row {
            flex-direction: column;
            gap: 8px;
            padding: 11px 6px;
          }
          .actionplan-row-fields {
            gap: 8px 0;
          }
          .actionplan-label, .risk-input, .risk-select {
            font-size: 15px;
          }
          .risk-add-btn {
            width: 100%;
            padding: 13px 0;
          }
          .risk-legend-list {
            flex-direction: column;
            gap: 8px;
          }
        }
      `}</style>
    </div>
  );
}
